/**
 * What the flow diagram needs beside the program itself: the formats a node
 * may be limited to, and the roles an actor may name.
 */

import { getDefinition, listRoles } from "@/lib/api/client";
import type { Variant } from "@/lib/scoring/types";

/**
 * What a node may be limited to — the lens, and the only legal values of
 * `appliesTo`.
 *
 * Both lists, because the engine matches against both: `_applies` tests a
 * node's `appliesTo` against `{fixture_type, config}`, so swimming's relay (a
 * kind of fixture) and cricket's T20 (a config) are the same kind of answer to
 * a node. They are read from the sport's published definition rather than typed
 * into the program, because that is where they already live and because the
 * publish gate checks every `appliesTo` against exactly this list — a lens
 * offering anything else would offer a value that cannot be published.
 *
 * A workflow no sport owns — Results, Games operations — has no formats, so it
 * gets none, and the lens hides itself.
 */
export async function variantsFor(sport: string | null): Promise<Variant[]> {
  if (!sport) return [];
  try {
    const definition = await getDefinition(sport);
    const types = (definition.fixtureTypes ?? []).map((type) => ({
      code: type.code,
      label: type.label || type.code,
      kind: "fixtureType" as const,
    }));
    const configs = (definition.configs ?? [])
      .filter((config) => !config.hidden)
      .map((config) => ({
        code: config.code,
        label: config.label || config.code,
        kind: "config" as const,
      }));
    return [...types, ...configs];
  } catch {
    // A sport with no published definition has no variants to offer; the lens
    // hides itself rather than the page failing.
    return [];
  }
}

/** Role codes an actor may name. The publish gate checks every actor against
 *  `role_dictionary`, so offering anything else would offer an unpublishable
 *  answer. */
export async function rolesFor(): Promise<string[]> {
  try {
    return [...new Set((await listRoles()).map((role) => role.code))].sort();
  } catch {
    return [];
  }
}
